import React, { useState, useEffect, useCallback } from "react";
import {
    Box, TextField, Button, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, CircularProgress, Stack, Grid
} from "@mui/material";
import { Print } from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { format, getDaysInMonth, startOfMonth, endOfMonth } from "date-fns";

function Attendancereport() {
    const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [attendanceMap, setAttendanceMap] = useState({});

    const year = parseInt(selectedMonth.split("-")[0]) || new Date().getFullYear();
    const month = (parseInt(selectedMonth.split("-")[1]) || (new Date().getMonth() + 1)) - 1;
    const daysInMonth = getDaysInMonth(new Date(year, month));
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const staffSnap = await getDocs(collection(db, "staff"));
            const staffData = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setStaffList(staffData);

            const start = format(startOfMonth(new Date(year, month)), "yyyy-MM-dd");
            const end = format(endOfMonth(new Date(year, month)), "yyyy-MM-dd");

            const q = query(
                collection(db, "staffattendance"),
                where("date", ">=", start),
                where("date", "<=", end)
            );

            const attSnap = await getDocs(q);
            const map = {};
            attSnap.docs.forEach(doc => {
                const data = doc.data();
                const key = `${data.staffId}_${data.date}`;
                map[key] = data.status;
            });
            setAttendanceMap(map);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
        }
        setLoading(false);
    }, [year, month]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusChar = (staffId, day) => {
        const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
        const status = attendanceMap[`${staffId}_${dateStr}`];
        if (status === "Present") return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>P</span>;
        if (status === "Absent") return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>A</span>;
        if (status === "Half Day") return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>H</span>;
        return "-";
    };

    const calculateTotal = (staffId) => {
        let p = 0, h = 0, a = 0;
        daysArray.forEach(day => {
            const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
            const res = attendanceMap[`${staffId}_${dateStr}`];
            if (res === "Present") p++;
            if (res === "Half Day") h++;
            if (res === "Absent") a++;
        });
        return { p, a, h, total: p + (h * 0.5) };
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Control Panel Header */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} className="no-print">
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Select Month" 
                        type="month" 
                        size="small"
                        value={selectedMonth} 
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={fetchData} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Load Report"}
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={() => window.print()} 
                        startIcon={<Print />}
                        disabled={loading || staffList.length === 0}
                    >
                        Print Report
                    </Button>
                </Stack>
            </Paper>

            {/* Target Printable Area */}
            <Grid container className="printable-area" sx={{ p: 2, flexDirection: 'column' }}>
                <TableContainer component={Paper} className="print-table-container" sx={{ borderRadius: 2, border: '1px solid #ccc', width: '100%' }}>
                    <Box textAlign="center" p={2}>
                        <Typography variant="h5" fontWeight="bold">MATOSHRI INFRA DHARASHIV PVT LTD</Typography>
                        <Typography variant="body2">Sarola, Dist. Dharashiv, Maharashtra</Typography>
                        <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 'medium' }}>
                            Monthly Attendance Report - {format(new Date(year, month), "MMMM yyyy")}
                        </Typography>
                    </Box>
                    <Table size="small" sx={{ minWidth: 1000 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={headerCell}>Sr.</TableCell>
                                <TableCell className="sticky-col-header" sx={{ ...headerCell, minWidth: 150, position: 'sticky', left: 0, zIndex: 10, bgcolor: '#f5f5f5' }}>
                                    Staff Name
                                </TableCell>
                                {daysArray.map(d => (
                                    <TableCell key={d} align="center" sx={dayCell}>{d}</TableCell>
                                ))}
                                <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e8f5e9', color: '#2e7d32' }}>P</TableCell>
                                <TableCell align="center" sx={{ ...headerCell, bgcolor: '#fff3e0', color: '#ed6c02' }}>H</TableCell>
                                <TableCell align="center" sx={{ ...headerCell, bgcolor: '#ffebee', color: '#d32f2f' }}>A</TableCell>
                                <TableCell align="center" sx={{ ...headerCell, bgcolor: '#e3f2fd', color: '#1976d2' }}>Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {staffList.map((staff, index) => {
                                const stats = calculateTotal(staff.id);
                                return (
                                    <TableRow key={staff.id} hover>
                                        <TableCell align="center" sx={{ border: '1px solid #ddd' }}>{index + 1}</TableCell>
                                        <TableCell className="sticky-col-cell" sx={{ fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 5, bgcolor: '#fff', border: '1px solid #ddd' }}>
                                            {staff.name}
                                        </TableCell>
                                        {daysArray.map(d => (
                                            <TableCell key={d} align="center" sx={dayCell}>
                                                {getStatusChar(staff.id, d)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>{stats.p}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>{stats.h}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'red', border: '1px solid #ddd' }}>{stats.a}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f0f7ff', border: '1px solid #ddd' }}>{stats.total}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            {/* Dynamic CSS Print Engine Overrides */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                    .printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        border: none !important; 
                    }
                    .no-print { display: none !important; }
                    
                    /* Force grid boundaries to render colors and layouts cleanly */
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; zoom: 70%; background-color: #fff !important; }
                    @page { size: A4 landscape; margin: 10mm; }
                    
                    /* Kill MUI Container horizontal scroll-shuttling wrappers */
                    .print-table-container { border: none !important; box-shadow: none !important; overflow: visible !important; }
                    table { border-collapse: collapse !important; width: 100% !important; table-layout: auto !important; }
                    
                    /* STRIP Sticky Positioning Layering Quirks during printing */
                    .sticky-col-header, .sticky-col-cell { 
                        position: static !important; 
                        background-color: transparent !important;
                    }

                    /* Unify explicit high-contrast border lines over cells */
                    th, td { 
                        border: 1px solid #000000 !important; 
                        font-size: 10px !important; 
                        padding: 4px 2px !important; 
                    }
                }
            `}</style>
        </Box>
    );
}

const headerCell = { fontWeight: 'bold', border: '1px solid #ddd', fontSize: '0.75rem', p: 1, textAlign: 'center' };
const dayCell = { border: '1px solid #ddd', p: 0.5, fontSize: '0.7rem', minWidth: '25px', textAlign: 'center' };

export default Attendancereport;